import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, Download, Filter, X, Eye, ShoppingBag } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { adminAPI } from '@/utils/adminApi';
import { formatCOP } from '@/utils/format';

const STATUS_LABELS = {
  pending: { label: 'Pendiente', className: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
  paid: { label: 'Pagado', className: 'bg-blue-50 text-blue-800 border-blue-200' },
  processing: { label: 'En preparación', className: 'bg-purple-50 text-purple-800 border-purple-200' },
  shipped: { label: 'Enviado', className: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  delivered: { label: 'Entregado', className: 'bg-green-50 text-green-800 border-green-200' },
  cancelled: { label: 'Cancelado', className: 'bg-red-50 text-red-800 border-red-200' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [skip, setSkip] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [tracking, setTracking] = useState('');
  const [notes, setNotes] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const limit = 20;

  // Debounce search input (300ms) to avoid API spam on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.listOrders({ status: statusFilter, search: debouncedSearch, date_from: dateFrom, date_to: dateTo, skip, limit });
      setOrders(data.orders || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch, dateFrom, dateTo, skip]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const openDetail = async (orderId) => {
    setDetailLoading(true);
    setSelectedOrder({});
    try {
      const order = await adminAPI.getOrder(orderId);
      setSelectedOrder(order);
      setTracking(order.tracking_number || '');
      setNotes(order.admin_notes || '');
    } catch (e) {
      alert('No se pudo cargar la orden');
      setSelectedOrder(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const changeStatus = async (newStatus) => {
    if (!selectedOrder?.id) return;
    setUpdating(true);
    try {
      await adminAPI.updateOrderStatus(selectedOrder.id, { status: newStatus, tracking_number: tracking, notes });
      setSelectedOrder({ ...selectedOrder, status: newStatus, tracking_number: tracking, admin_notes: notes });
      await fetchOrders();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al actualizar el estado');
    } finally {
      setUpdating(false);
    }
  };

  const saveMeta = async () => {
    if (!selectedOrder?.id) return;
    setSavingMeta(true);
    try {
      await adminAPI.updateOrderStatus(selectedOrder.id, {
        status: selectedOrder.status,
        tracking_number: tracking,
        notes,
      });
      setSelectedOrder({ ...selectedOrder, tracking_number: tracking, admin_notes: notes });
      await fetchOrders();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingMeta(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setSkip(0);
  };

  const csvUrl = adminAPI.exportOrdersCSVUrl({ status: statusFilter, date_from: dateFrom, date_to: dateTo });

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Panel administrativo</p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight" data-testid="admin-orders-title">Pedidos</h1>
            <p className="text-[#57534E] mt-2">{total} {total === 1 ? 'pedido' : 'pedidos'} encontrados</p>
          </div>
          <a href={csvUrl} className="inline-flex" data-testid="admin-export-csv">
            <Button className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-5">
              <Download className="w-4 h-4 mr-2" /> Exportar CSV
            </Button>
          </a>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="admin-orders-filters">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8BA58F]" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
              placeholder="Buscar por orden, cliente, email..."
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
              data-testid="admin-orders-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setSkip(0); }}
            className="px-4 py-2.5 rounded-full border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="admin-orders-status-filter"
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setSkip(0); }}
            className="px-4 py-2.5 rounded-full border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="admin-orders-date-from"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setSkip(0); }}
            className="px-4 py-2.5 rounded-full border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="admin-orders-date-to"
          />
          {(statusFilter || search || dateFrom || dateTo) && (
            <button onClick={clearFilters} className="text-xs text-[#8BA58F] hover:text-[#2A5C43] flex items-center gap-1 justify-center md:col-span-5" data-testid="admin-orders-clear-filters">
              <X className="w-3 h-3" /> Limpiar filtros
            </button>
          )}
        </div>

        {/* Orders table */}
        <div className="bg-white rounded-3xl border border-[#E7E5E4] overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16" data-testid="admin-orders-empty">
              <ShoppingBag className="w-12 h-12 text-[#8BA58F] mx-auto mb-4" />
              <p className="text-[#57534E]">No hay pedidos que coincidan con los filtros</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-sm" data-testid="admin-orders-table">
                <thead className="bg-[#FAF9F5] text-xs uppercase tracking-widest text-[#8BA58F]">
                  <tr>
                    <th className="text-left px-6 py-3">Orden</th>
                    <th className="text-left px-6 py-3">Fecha</th>
                    <th className="text-left px-6 py-3">Cliente</th>
                    <th className="text-left px-6 py-3">Items</th>
                    <th className="text-right px-6 py-3">Total</th>
                    <th className="text-center px-6 py-3">Estado</th>
                    <th className="text-right px-6 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F5F5F4]">
                  {orders.map((o) => {
                    const st = STATUS_LABELS[o.status] || STATUS_LABELS.pending;
                    return (
                      <tr key={o.id} className="hover:bg-[#FAF9F5]" data-testid={`admin-order-row-${o.order_number}`}>
                        <td className="px-6 py-4 font-medium text-[#1C1917] whitespace-nowrap">{o.order_number}</td>
                        <td className="px-6 py-4 text-[#57534E] whitespace-nowrap">{formatDate(o.created_at)}</td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-[#1C1917]">{o.customer_name}</p>
                          <p className="text-xs text-[#57534E]">{o.customer_email}</p>
                        </td>
                        <td className="px-6 py-4 text-[#57534E]">{o.items?.length || 0}</td>
                        <td className="px-6 py-4 text-right font-display font-bold text-[#1C1917]">{formatCOP(o.total)}</td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${st.className}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => openDetail(o.id)}
                            className="text-[#2A5C43] hover:underline text-sm font-medium inline-flex items-center gap-1"
                            data-testid={`admin-view-order-${o.order_number}`}
                          >
                            <Eye className="w-4 h-4" /> Ver
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {total > limit && (
                <div className="p-4 border-t border-[#E7E5E4] flex items-center justify-between">
                  <p className="text-xs text-[#57534E]">
                    Mostrando {skip + 1}-{Math.min(skip + limit, total)} de {total}
                  </p>
                  <div className="flex gap-2">
                    <button
                      disabled={skip === 0}
                      onClick={() => setSkip(Math.max(0, skip - limit))}
                      className="px-3 py-1.5 text-xs rounded-full border border-[#E7E5E4] disabled:opacity-40"
                    >Anterior</button>
                    <button
                      disabled={skip + limit >= total}
                      onClick={() => setSkip(skip + limit)}
                      className="px-3 py-1.5 text-xs rounded-full border border-[#E7E5E4] disabled:opacity-40"
                    >Siguiente</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order detail modal */}
        {selectedOrder !== null && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-start lg:items-center justify-center p-4 overflow-y-auto"
            onClick={() => setSelectedOrder(null)}
            data-testid="admin-order-detail-modal"
          >
            <div
              className="bg-white rounded-3xl w-full max-w-3xl my-8 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {detailLoading || !selectedOrder.id ? (
                <div className="p-12 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" />
                </div>
              ) : (
                <>
                  <div className="p-6 border-b border-[#F5F5F4] flex items-start justify-between">
                    <div>
                      <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-1">Pedido</p>
                      <h2 className="font-display font-bold text-2xl text-[#1C1917]">{selectedOrder.order_number}</h2>
                      <p className="text-xs text-[#57534E] mt-1">{formatDate(selectedOrder.created_at)}</p>
                    </div>
                    <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-[#F7F2E8] rounded-full" data-testid="admin-close-detail">
                      <X className="w-5 h-5 text-[#57534E]" />
                    </button>
                  </div>

                  <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                    {/* Customer + address */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold mb-2">Cliente</h4>
                        <p className="font-medium text-[#1C1917]">{selectedOrder.customer_name}</p>
                        <p className="text-sm text-[#57534E]">{selectedOrder.customer_email}</p>
                        <p className="text-sm text-[#57534E]">{selectedOrder.customer_phone}</p>
                        {selectedOrder.customer_document && (
                          <p className="text-sm text-[#57534E]">Doc: {selectedOrder.customer_document}</p>
                        )}
                      </div>
                      <div>
                        <h4 className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold mb-2">Dirección de envío</h4>
                        <p className="text-sm text-[#1C1917]">{selectedOrder.shipping_address?.address}</p>
                        <p className="text-sm text-[#57534E]">
                          {selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.department}
                        </p>
                        {selectedOrder.shipping_address?.neighborhood && (
                          <p className="text-sm text-[#57534E]">{selectedOrder.shipping_address.neighborhood}</p>
                        )}
                      </div>
                    </div>

                    {/* Items */}
                    <div>
                      <h4 className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold mb-3">Productos</h4>
                      <div className="border border-[#E7E5E4] rounded-2xl divide-y divide-[#F5F5F4]">
                        {selectedOrder.items?.map((it, i) => (
                          <div key={i} className="p-4 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm text-[#1C1917]">{it.product_name}</p>
                              <p className="text-xs text-[#57534E]">{it.size} • SKU {it.sku} • x{it.quantity}</p>
                            </div>
                            <p className="font-display font-bold text-[#1C1917]">{formatCOP(it.subtotal)}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Totals */}
                    <div className="bg-[#F7F2E8]/50 rounded-2xl p-4 space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-[#57534E]">Subtotal</span><span>{formatCOP(selectedOrder.subtotal)}</span></div>
                      <div className="flex justify-between"><span className="text-[#57534E]">Envío</span><span>{formatCOP(selectedOrder.shipping_cost)}</span></div>
                      {selectedOrder.discount > 0 && (
                        <div className="flex justify-between text-[#2A5C43]"><span>Descuento</span><span>-{formatCOP(selectedOrder.discount)}</span></div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-[#E7E5E4] font-display font-bold text-lg text-[#1C1917]">
                        <span>Total</span><span>{formatCOP(selectedOrder.total)}</span>
                      </div>
                    </div>

                    {/* Tracking + notes */}
                    <div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold block mb-1">N° de guía</label>
                          <input
                            value={tracking}
                            onChange={(e) => setTracking(e.target.value)}
                            placeholder="Ej. YT12345"
                            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
                            data-testid="admin-order-tracking"
                          />
                        </div>
                        <div>
                          <label className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold block mb-1">Notas internas</label>
                          <input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Notas del pedido"
                            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
                            data-testid="admin-order-notes"
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={saveMeta}
                          disabled={savingMeta}
                          className="text-xs font-semibold text-[#2A5C43] hover:underline disabled:opacity-50"
                          data-testid="admin-order-save-meta"
                        >
                          {savingMeta ? 'Guardando...' : 'Guardar guía y notas'}
                        </button>
                      </div>
                    </div>

                    {/* Status transitions */}
                    <div>
                      <h4 className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold mb-3">Cambiar estado</h4>
                      <div className="flex flex-wrap gap-2" data-testid="admin-order-status-actions">
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <button
                            key={k}
                            disabled={updating || selectedOrder.status === k}
                            onClick={() => changeStatus(k)}
                            className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors disabled:opacity-40 ${
                              selectedOrder.status === k
                                ? 'bg-[#2A5C43] text-white border-[#2A5C43]'
                                : 'border-[#E7E5E4] text-[#1C1917] hover:border-[#2A5C43] hover:text-[#2A5C43]'
                            }`}
                            data-testid={`admin-order-set-${k}`}
                          >
                            {v.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
